import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Layout from "../components/app/Layout";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import api from "../lib/api";
import { Inbox, Phone, Mail as MailIcon, MessageCircle, Check, Archive, Trophy, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/**
 * /academy/applications — dedicated standalone Applications inbox for the
 * academy role (lead-gen). Pulled out of AcademyDashboard in Iter 29 so the
 * sidebar nav reflects strict role-based separation.
 */
export default function AcademyApplicationsPage() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/academy/applications");
      setList(data || []);
    } catch (_) {
      setList([]);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (user && user.role !== "academy") return <Navigate to="/" replace />;

  const filtered = list.filter((a) => filter === "all" || a.status === filter);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/academy/applications/${id}`, { status });
      toast.success(lang === "bg" ? "Обновено" : "Updated");
      load();
    } catch (_) { toast.error("Error"); }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="academy-applications-page">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="label-mono mb-1">// {lang === "bg" ? "Заявки от родители" : "Inquiries inbox"}</div>
            <h1 className="font-oswald uppercase text-3xl sm:text-4xl tracking-tight">
              {lang === "bg" ? "Заявки" : "Applications"}
            </h1>
            <p className="text-xs text-white/45 font-mono mt-1">
              {lang === "bg"
                ? "Всички запитвания от родители и играчи към твоята академия."
                : "All inquiries from parents and players to your academy."}
            </p>
          </div>
          <button onClick={load} className="btn-secondary px-3 py-2 text-[10px] flex items-center gap-1.5" data-testid="apps-refresh">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> {lang === "bg" ? "Обнови" : "Refresh"}
          </button>
        </div>

        <div className="inline-flex border border-white/10 rounded overflow-hidden">
          {[
            { id: "all", bg: "Всички", en: "All" },
            { id: "new", bg: "Нови", en: "New" },
            { id: "contacted", bg: "Свързани", en: "Contacted" },
            { id: "converted", bg: "Записани", en: "Converted" },
            { id: "archived", bg: "Архив", en: "Archived" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-2 text-[11px] font-mono uppercase tracking-widest ${filter === f.id ? "bg-pitch text-white" : "text-white/55 hover:text-white"}`}
              data-testid={`apps-page-filter-${f.id}`}
            >
              {lang === "bg" ? f.bg : f.en}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="card-sharp p-12 flex items-center justify-center">
            <div className="pitch-loader"><div className="ball" /></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="card-sharp p-12 text-center" data-testid="apps-page-empty">
            <Inbox className="w-10 h-10 text-pitch/40 mx-auto mb-3" />
            <div className="font-oswald uppercase text-lg tracking-tight">
              {lang === "bg" ? "Все още няма заявки" : "No applications yet"}
            </div>
            <div className="text-white/35 text-xs font-mono mt-1">
              {lang === "bg"
                ? "Заявките от публичния ти профил ще се появят тук."
                : "Inquiries from your public profile will land here."}
            </div>
          </div>
        ) : (
          <div className="space-y-2" data-testid="apps-page-list">
            {filtered.map((a) => (
              <div key={a.id} className="surface-1 p-4 rounded" data-testid={`apps-row-${a.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-oswald uppercase tracking-tight text-base">{a.parent_name}</span>
                      <span className={`chip-badge ${a.status === "new" ? "gold" : ""}`}>{a.status.toUpperCase()}</span>
                    </div>
                    <div className="text-[11px] text-white/45 font-mono flex items-center gap-3 flex-wrap mt-1">
                      {a.parent_email && (
                        <a href={`mailto:${a.parent_email}`} className="hover:text-pitch flex items-center gap-1">
                          <MailIcon className="w-3 h-3" /> {a.parent_email}
                        </a>
                      )}
                      {a.parent_phone && (
                        <a href={`tel:${a.parent_phone}`} className="hover:text-pitch flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {a.parent_phone}
                        </a>
                      )}
                      <span>{new Date(a.created_at).toLocaleDateString(lang === "bg" ? "bg-BG" : "en-US", { day: "2-digit", month: "short", year: "numeric" })}</span>
                    </div>
                    {a.player_name && (
                      <div className="text-xs text-white/65 mt-2 flex flex-wrap items-center gap-2">
                        <Trophy className="w-3 h-3 text-pitch" />
                        <span className="font-oswald uppercase tracking-tight">{a.player_name}</span>
                        {a.player_age != null && <span className="text-white/40">· {a.player_age}{lang === "bg" ? " г." : "y"}</span>}
                        {a.player_level && <span className="chip-badge text-[9px]">{a.player_level}</span>}
                      </div>
                    )}
                    {a.message && (
                      <div className="text-xs text-white/70 mt-2 whitespace-pre-wrap border-l-2 border-pitch/40 pl-3">
                        {a.message}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {a.status !== "contacted" && (
                      <button onClick={() => setStatus(a.id, "contacted")} className="p-2 text-white/55 hover:text-pitch hover:bg-pitch/5 rounded transition-colors" title={lang === "bg" ? "Свързан" : "Mark contacted"} data-testid={`apps-row-contacted-${a.id}`}>
                        <MessageCircle className="w-4 h-4" />
                      </button>
                    )}
                    {a.status !== "converted" && (
                      <button onClick={() => setStatus(a.id, "converted")} className="p-2 text-white/55 hover:text-pitch hover:bg-pitch/5 rounded transition-colors" title={lang === "bg" ? "Записан" : "Mark converted"} data-testid={`apps-row-converted-${a.id}`}>
                        <Check className="w-4 h-4" />
                      </button>
                    )}
                    {a.status !== "archived" && (
                      <button onClick={() => setStatus(a.id, "archived")} className="p-2 text-white/40 hover:text-red-400 hover:bg-red-500/5 rounded transition-colors" title={lang === "bg" ? "Архив" : "Archive"} data-testid={`apps-row-archived-${a.id}`}>
                        <Archive className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
